import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    Output,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { BehaviorSubject, filter } from 'rxjs';

import { ProgressBarComponent } from '../progress-bar.component';

@Component({
    selector: 'ms-timer-progress-bar',
    templateUrl: 'timer-progress-bar.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ProgressBarComponent],
})
export class TimerProgressBarComponent implements AfterViewInit, OnChanges {
    private readonly destroyRef = inject(DestroyRef);

    @HostBinding('class.ms-timer-progress-bar') cssClass = true;

    @Input() totalDuration!: number;
    @Input() pauseProgressBar = false;
    @Input() smoothProgressTransition = false;
    @Input() shouldShowInformation = false;
    @Input() shouldShowPercentage = true;
    @Input() informationText: string | undefined;
    @Input() step = 100;
    @Input() loop = true;
    @Input() shouldStop = false;
    @Output() complete = new EventEmitter<boolean>();
    @Output() progress = new EventEmitter<number>();

    private value = 0;
    animationDuration = 0;

    private progressBarInterval: (() => void) | null = null;
    value$ = new BehaviorSubject<number>(0);

    constructor(private timerService: TimerService) {
        this.destroyRef.onDestroy(() => {
            this.timerService.removeAll();
            this.stopProgressBarInterval();
        });
    }

    ngOnChanges(changes: ISimpleChanges<TimerProgressBarComponent>): void {
        if (changes.shouldStop) {
            this.value$.complete();
        }

        if (changes.step) {
            this.step = changes.step.currentValue || 100;
        }

        if (changes.totalDuration) {
            this.animationDuration = this.totalDuration / 100;
        }
    }

    ngAfterViewInit(): void {
        this.value$
            .pipe(
                filter(() => !this.pauseProgressBar),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((value) => {
                if (Math.ceil(value) >= 100) {
                    this.complete.emit();
                    this.stopProgressBarInterval();

                    if (this.loop) {
                        this.resetProgressBar();
                    } else {
                        this.value$.complete();
                    }
                }
            });

        this.resetProgressBar();
    }

    resetProgressBar(): void {
        this.value = 0;
        this.stopProgressBarInterval();
        this.startProgressbar();
    }

    private startProgressbar(): void {
        this.progressBarInterval = this.timerService.setIntervalOutsideAngular(() => {
            if (!this.pauseProgressBar) {
                const slice = 100 / this.step;
                this.value += slice;
                this.value$.next(this.value);
                this.progress.emit(this.value);
            }
        }, this.totalDuration / this.step);
    }

    private stopProgressBarInterval = () => {
        this.progressBarInterval?.();
        this.progressBarInterval = null;
    };
}
